import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { MapPin, Clock, DollarSign, Building2, Bookmark, ArrowLeft, CheckCircle2, Briefcase } from "lucide-react";
import { toast } from "sonner";
import ShareJob from "../components/ShareJob";

export default function JobDetail() {
  const { id } = useParams();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState(false);
  const [coverLetter, setCoverLetter] = useState("");
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    api.get(`/jobs/${id}`).then(r => setJob(r.data)).finally(() => setLoading(false));
    if (user?.role === "candidate") {
      api.get("/applications/mine").then(r => {
        if (r.data.some(a => a.job_id === id)) setApplied(true);
      });
    }
  }, [id, user]);

  const apply = async () => {
    if (!user) { toast.info("Please sign in as a candidate to apply."); nav("/login"); return; }
    if (user.role !== "candidate") { toast.error("Only candidates can apply."); return; }
    setApplying(true);
    try {
      await api.post("/applications", { job_id: id, cover_letter: coverLetter });
      toast.success("Application submitted!");
      setApplied(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Application failed");
    } finally { setApplying(false); }
  };

  if (loading) return <div className="ae-container py-20 text-center text-slate-500">Loading…</div>;
  if (!job) return <div className="ae-container py-20 text-center">Job not found</div>;
  const isOpen = job.is_active !== false && (!job.expires_at || job.expires_at > new Date().toISOString());

  return (
    <div data-testid="job-detail-page">
      <section className="bg-[#0F2A4A] text-white py-16">
        <div className="ae-container">
          <Link to="/jobs" className="inline-flex items-center gap-2 text-white/70 hover:text-[#C5A059] text-sm mb-6" data-testid="back-to-jobs">
            <ArrowLeft className="w-4 h-4" /> Back to Jobs
          </Link>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <div className="ae-overline mb-3">{job.industry}</div>
              <h1 className="text-3xl md:text-5xl font-light" data-testid="job-title">{job.title}</h1>
              <div className="mt-3 text-white/80 flex items-center gap-2">
                <Building2 className="w-4 h-4" /> {job.company_name}
              </div>
              <div className="flex flex-wrap gap-4 mt-4 text-sm text-white/70">
                <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {job.location}</span>
                <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> {job.experience_min}-{job.experience_max} yrs</span>
                {job.salary_min && <span className="flex items-center gap-1"><DollarSign className="w-4 h-4" /> ₹{(job.salary_min/100000).toFixed(1)}-{(job.salary_max/100000).toFixed(1)} LPA</span>}
                <span className="flex items-center gap-1"><Briefcase className="w-4 h-4" /> {job.employment_type.replace("_", " ")}</span>
              </div>
            </div>
            <div className="flex flex-col gap-2 md:min-w-[220px]">
              {!isOpen ? (
                <div className="bg-white/10 border border-white/20 text-white rounded-xl px-5 py-3 text-sm text-center" data-testid="job-closed-badge">
                  {job.status === "on_hold" ? "This position is on hold" : "This position is closed"}
                </div>
              ) : applied ? (
                <div className="bg-emerald-500 text-white rounded-xl px-5 py-3 flex items-center gap-2" data-testid="applied-badge">
                  <CheckCircle2 className="w-5 h-5" /> Application Submitted
                </div>
              ) : (
                <button onClick={apply} disabled={applying} className="ae-btn-primary" data-testid="apply-btn">
                  {applying ? "Applying…" : "Apply Now"}
                </button>
              )}
              <ShareJob job={job} label="Share Job" testid="share"
                triggerClassName="w-full border border-white/30 text-white rounded-xl px-5 py-3 hover:bg-white/10 transition-colors flex items-center gap-2 justify-center text-sm" />
              <button className="border border-white/30 text-white rounded-xl px-5 py-3 hover:bg-white/10 transition-all flex items-center gap-2 justify-center text-sm" data-testid="save-btn">
                <Bookmark className="w-4 h-4" /> Save Job
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="ae-container grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8 space-y-10">
            <Block title="Job Description"><p className="text-slate-600 leading-relaxed">{job.description}</p></Block>
            {job.responsibilities?.length > 0 && (
              <Block title="Key Responsibilities">
                <ul className="space-y-3">
                  {job.responsibilities.map((r, i) => (
                    <li key={i} className="flex gap-3 text-slate-600 leading-relaxed">
                      <CheckCircle2 className="w-5 h-5 text-[#C5A059] flex-shrink-0 mt-0.5" /> {r}
                    </li>
                  ))}
                </ul>
              </Block>
            )}
            {job.requirements?.length > 0 && (
              <Block title="Requirements">
                <ul className="space-y-3">
                  {job.requirements.map((r, i) => (
                    <li key={i} className="flex gap-3 text-slate-600 leading-relaxed">
                      <CheckCircle2 className="w-5 h-5 text-[#C5A059] flex-shrink-0 mt-0.5" /> {r}
                    </li>
                  ))}
                </ul>
              </Block>
            )}
            {user?.role === "candidate" && !applied && isOpen && (
              <Block title="Cover Letter (optional)">
                <textarea rows={5} value={coverLetter} onChange={e => setCoverLetter(e.target.value)}
                  data-testid="cover-letter"
                  placeholder="Tell the recruiter why you're a great fit…"
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-[#C5A059] outline-none px-4 py-3 text-sm" />
              </Block>
            )}
          </div>

          <aside className="lg:col-span-4 space-y-4">
            <div className="ae-card p-6">
              <h4 className="font-heading font-semibold text-[#0F2A4A] mb-4">Skills Required</h4>
              <div className="flex flex-wrap gap-2">
                {(job.skills || []).map(s => (
                  <span key={s} className="text-xs px-3 py-1.5 rounded-full bg-[#C5A059]/10 text-[#0F2A4A] border border-[#C5A059]/20">{s}</span>
                ))}
              </div>
            </div>
            <div className="ae-card p-6 space-y-4 text-sm">
              <h4 className="font-heading font-semibold text-[#0F2A4A]">Job Details</h4>
              <Row k="Company" v={job.company_name} />
              <Row k="Location" v={job.location} />
              <Row k="Experience" v={`${job.experience_min}-${job.experience_max} years`} />
              {job.qualification && <Row k="Qualification" v={job.qualification} />}
              <Row k="Employment" v={job.employment_type.replace("_", " ")} />
              <Row k="Industry" v={job.industry} />
              {job.department && <Row k="Department" v={job.department} />}
              {job.salary_min && <Row k="CTC" v={`₹${(job.salary_min/100000).toFixed(1)} - ${(job.salary_max/100000).toFixed(1)} LPA`} />}
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}

const Block = ({ title, children }) => (
  <div>
    <h2 className="font-heading font-semibold text-xl text-[#0F2A4A] mb-4 ae-gold-underline">{title}</h2>
    {children}
  </div>
);
const Row = ({ k, v }) => (
  <div className="flex justify-between gap-2 pb-2 border-b border-slate-100 last:border-0">
    <span className="text-slate-500 capitalize">{k}</span>
    <span className="text-[#0F2A4A] font-medium text-right capitalize">{v}</span>
  </div>
);
